/**
 * Maxlive88 — PWA Push Notification helpers.
 *
 * Tiny wrapper around the browser Push API + the backend /push endpoints.
 *
 * Usage:
 *   import { pushManager } from "../lib/push";
 *   await pushManager.subscribe("/api/aff-panel/push");
 *   await pushManager.unsubscribe("/api/aff-panel/push");
 *   await pushManager.test("/api/aff-panel/push");
 */
import api from "./api";

function urlBase64ToUint8Array(b64) {
  const pad = "=".repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + pad).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function isSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

async function registerSW() {
  if (!isSupported()) return null;
  try {
    return await navigator.serviceWorker.register("/sw.js", { scope: "/" });
  } catch (_e) {
    return null;
  }
}

async function getPublicKey() {
  const { data } = await api.get("/push/public-key");
  return data?.public_key || "";
}

async function permissionState() {
  if (!isSupported()) return "unsupported";
  return Notification.permission || "default";
}

async function ensurePermission() {
  if (!isSupported()) return false;
  if (Notification.permission === "granted") return true;
  if (Notification.permission === "denied") return false;
  const r = await Notification.requestPermission();
  return r === "granted";
}

async function currentSubscription() {
  if (!isSupported()) return null;
  const reg = await navigator.serviceWorker.ready.catch(() => null);
  if (!reg) return null;
  return await reg.pushManager.getSubscription().catch(() => null);
}

async function subscribe(apiBase = "/aff-panel/push") {
  if (!isSupported()) throw new Error("Push not supported in this browser");
  const granted = await ensurePermission();
  if (!granted) throw new Error("Notification permission denied");
  const reg = (await registerSW()) || (await navigator.serviceWorker.ready);
  const publicKey = await getPublicKey();
  if (!publicKey) throw new Error("Server push key missing");
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }
  const json = sub.toJSON();
  await api.post(`${apiBase}/subscribe`, {
    endpoint: json.endpoint,
    keys: json.keys,
    user_agent: navigator.userAgent.slice(0, 280),
  });
  return sub;
}

async function unsubscribe(apiBase = "/aff-panel/push") {
  const sub = await currentSubscription();
  if (!sub) return { unsubscribed: false };
  try {
    await api.post(`${apiBase}/unsubscribe`, { endpoint: sub.endpoint });
  } catch (_e) { /* ignore server error — still revoke local */ }
  await sub.unsubscribe().catch(() => {});
  return { unsubscribed: true };
}

async function sendTest(apiBase = "/aff-panel/push") {
  const { data } = await api.post(`${apiBase}/test`, {});
  return data;
}

async function status(apiBase = "/aff-panel/push") {
  try {
    const { data } = await api.get(`${apiBase}/status`);
    const sub = await currentSubscription();
    return {
      supported: isSupported(),
      permission: await permissionState(),
      subscribed_local: !!sub,
      ...data,
    };
  } catch (_e) {
    return {
      supported: isSupported(),
      permission: await permissionState(),
      subscribed_local: !!(await currentSubscription()),
      subscriptions: 0,
      configured: false,
    };
  }
}

export const pushManager = {
  isSupported,
  permissionState,
  ensurePermission,
  currentSubscription,
  subscribe,
  unsubscribe,
  sendTest,
  status,
};
